import { useState, useEffect, useCallback } from "react";
import { Trash2, Loader2, Database, HardDrive, Layers, AlertTriangle, BarChart3, Settings, Search, Wrench, IndianRupee, History, Users, Archive } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const PROTECTED_COLLECTIONS = ["users", "system.indexes", "system.profile"];

// Runtime / framework state — user should never delete these from the UI
// (they hold scheduler locks, dedupe flags, deploy markers, chunked-upload
// bookkeeping, etc). Still visible under Ensure Indexes / Delete All Data.
const HIDDEN_COLLECTIONS = new Set([
  // Framework runtime
  "scheduler_locks", "system_config", "job_dedupe_status",
  "deploy_bootstrap_markers", "export_cache_meta",
  "unit_mismatch_cache_meta", "active_units_meta",
  "aum_sync_meta", "nav_history_meta",
  // Chunked upload bookkeeping
  "chunked_uploads", "chunked_uploads_data",
  // Empty-but-referenced (future use / retired flows)
  "karvy_transactions", "active_units_worker_errors",
  "cams_feed_logs", "cams_report_status",
  "commission_chunks", "commission_job_state",
  "client_reporting_prefs", "reporting_preferences",
  "mfd_leads", "mfd_subscriptions",
  "nav_grandfathering",
  "portfolio_strategies", "portfolio_strategy_versions",
  "reconciliation_exceptions", "reconciliation_logs", "reconciliation_summary",
  "remap_status", "upload_errors",
  "failed_transactions", "goals", "tenants",
  // Virtual/duplicate view of subbroker collection — surfaced as its own
  // card here would be misleading (they share the same underlying rows).
  "associate_master",
]);

const collectionGroups = [
  {
    title: "Account Creation Master",
    subtitle: "Reference tables used when creating a new investor / account.",
    icon: Layers,
    items: [
      { id: "gender", label: "Gender", description: "Gender master — Code (M/F/O) + Name (Male/Female/Other). Seeded on first boot; edit via Account Creation Master UI.", iconBg: "bg-pink-500/10 text-pink-400" },
      { id: "country", label: "Country", description: "ISO 3166-1 Alpha-3 country codes + international dial codes. Seeded from pycountry on first boot; ~242 rows.", iconBg: "bg-blue-500/10 text-blue-400" },
    ],
  },
  {
    title: "Raw Files",
    icon: HardDrive,
    items: [
      { id: "MFSD201", label: "MFSD201 (Karvy)", description: "Karvy daily transaction feed — every buy/sell/switch/STP/SIP row. Ingested from Karvy emails, feeds Transaction Master.", iconBg: "bg-teal-500/10 text-teal-400" },
      { id: "MFSD203", label: "MFSD203 (Karvy)", description: "Karvy balance-units snapshot — folio × scheme × current units. Feeds AUM Master for Karvy-serviced AMCs.", iconBg: "bg-teal-500/10 text-teal-400" },
      { id: "MFSD211", label: "MFSD211 (Karvy)", description: "Karvy investor demographics — PAN, name, DOB, KYC, address, contact. Feeds Investor Master.", iconBg: "bg-teal-500/10 text-teal-400" },
      { id: "WBR2", label: "WBR2 (CAMS)", description: "CAMS daily transaction feed — buy/sell/switch/STP/SIP rows for CAMS-serviced AMCs. Feeds Transaction Master.", iconBg: "bg-sky-500/10 text-sky-400" },
      { id: "WBR2A", label: "WBR2A (CAMS)", description: "CAMS historical transactions — full lifetime backfill (pre-2018 legacy trades). One-time load per client.", iconBg: "bg-sky-500/10 text-sky-400" },
      { id: "WBR9", label: "WBR9 (CAMS)", description: "CAMS investor demographics + balance units — combined investor/holdings snapshot. Feeds Investor + AUM Master.", iconBg: "bg-sky-500/10 text-sky-400" },
      { id: "WBR9D", label: "WBR9D (CAMS)", description: "CAMS investor master historical — snapshot of demographics before latest WBR9 refresh.", iconBg: "bg-sky-500/10 text-sky-400" },
    ],
  },
  {
    title: "Master Data",
    icon: Layers,
    items: [
      { id: "investor_master", label: "Investor Master", description: "Unified investor ledger — PAN × folio × demographics across CAMS + Karvy. Single source of truth for KYC/contact data.", iconBg: "bg-emerald-500/10 text-emerald-400" },
      { id: "aum_master", label: "AUM Master", description: "Current holdings ledger — folio × scheme × units × NAV × market value. Refreshed daily; source of truth for portfolios.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "transaction_master", label: "Transaction Master", description: "Unified chronological transaction ledger — 17 canonical columns from CAMS + Karvy. Drives Portfolio & XIRR.", iconBg: "bg-emerald-500/10 text-emerald-400" },
      { id: "junk_transactions", label: "Junk Transactions", description: "Non-financial + rejected rows (KYC updates, address changes, failed orders). Cosmetic — excluded from portfolio math.", iconBg: "bg-gray-500/10 text-gray-400" },
    ],
  },
  {
    title: "AMFI Master",
    subtitle: "Scheme catalogs, NAV history & code mappings",    icon: BarChart3,
    items: [
      { id: "scheme_master", label: "Scheme Master", description: "BSE StarMF scheme catalog filtered to schemes with active AMFI NAV. Used for order-placing UI.", iconBg: "bg-indigo-500/10 text-indigo-400" },
      { id: "scheme_data", label: "Scheme Data (AMFI)", description: "AMFI-published scheme catalog with categories (Equity/Debt/Hybrid), ISINs, and AMFI codes. Used for classification.", iconBg: "bg-orange-500/10 text-orange-400" },
      { id: "nav_master", label: "NAV Master", description: "Daily NAV history + 3Y, 5Y, and 31-Jan-2018 grandfathering anchors. Powers XIRR, CAGR, and LTCG tax calcs.", iconBg: "bg-amber-500/10 text-amber-400" },
      { id: "bse_scheme_raw", label: "BSE Scheme Raw", description: "Full 43-column BSE StarMF export — pre-filter source for scheme_master. Rarely queried directly.", iconBg: "bg-indigo-500/10 text-indigo-400" },
      { id: "scheme_master_pulse_labs", label: "Pulse Labs Scheme Master", description: "Scheme Code ↔ Product Code cross-reference — bridges legacy Pulse Labs codes to BSE/AMFI codes.", iconBg: "bg-amber-500/10 text-amber-400" },
    ],
  },
  {
    title: "Commission",
    subtitle: "Brokerage feeds & payout reconciliation",
    icon: IndianRupee,
    items: [
      { id: "WBR77", label: "WBR77 (CAMS)", description: "CAMS brokerage payout report — per-folio commission earned. Reconciled against active_units to catch unpaid trails.", iconBg: "bg-sky-500/10 text-sky-400" },
      { id: "MFSD205", label: "MFSD205 (Karvy)", description: "Karvy brokerage payout report — per-folio commission earned. Feeds the Unpaid Brokerage view alongside WBR77.", iconBg: "bg-teal-500/10 text-teal-400" },
      { id: "VOLTMONEY", label: "Volt Money", description: "Volt Money loan-against-MF commission feed. Uploaded via Commission Management for cross-source consolidation.", iconBg: "bg-amber-500/10 text-amber-400" },
    ],
  },
  {
    title: "Files and Chunks",
    subtitle: "GridFS buckets — every large file stored in Mongo lives here as a (metadata, payload) pair. Never delete just one half.",
    icon: Archive,
    items: [
      { id: "commission_invoices.files", label: "Commission Invoices (metadata)", description: "GridFS INDEX for sub-broker invoice PDFs — one row per PDF with {job_id, period, sub_broker_code, filename, size}. Must be deleted together with .chunks.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "commission_invoices.chunks", label: "Commission Invoices (payload)", description: "GridFS PAYLOAD for invoice PDFs — bytes split into 255 KB chunks, indexed by the .files row's _id. Deleting orphans the PDFs.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "commission_link_xlsx.files", label: "Commission Link XLSX (metadata)", description: "GridFS INDEX for oversized xlsx exports emailed as tokenised /d/{token} download links — {token, expires_at, download_count}. 7-day TTL.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "commission_link_xlsx.chunks", label: "Commission Link XLSX (payload)", description: "GridFS PAYLOAD for the link-download xlsx bytes. Streamed to the sub-broker when they click the tokenised link.", iconBg: "bg-gray-500/10 text-gray-400" },
    ],
  },
  {
    title: "Logs",
    subtitle: "Job runs & communication audits",
    icon: History,
    items: [
      { id: "commission_email_log", label: "Commission Email Log", description: "Audit trail of payout emails sent to sub-brokers — success + failure log per (job_id, sub_broker_code). Regulatory record.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "commission_runs", label: "Commission Runs", description: "Commission calculation job history — every 'Run Commissions' click creates one row with input files, period, and output stats.", iconBg: "bg-violet-500/10 text-violet-400" },
    ],
  },
  {
    title: "Family & Sub-broker",
    subtitle: "Client relationships & attribution",
    icon: Users,
    items: [
      { id: "client_master", label: "Client Master", description: "Deduplicated client roster after merging CAMS + Karvy investor rows. One row per unique client (PAN or PAN+DOB fallback).", iconBg: "bg-emerald-500/10 text-emerald-400" },
      { id: "family_mapping", label: "Family Mapping", description: "PAN → family-head PAN linkage — powers family-aggregate portfolio views (spouse, minor, HUF, etc).", iconBg: "bg-emerald-500/10 text-emerald-400" },
      { id: "subbroker_mapping", label: "Subbroker Mapping", description: "Folio → sub-broker attribution — determines who earns the trail on each folio. Drives commission payouts.", iconBg: "bg-emerald-500/10 text-emerald-400" },
      { id: "subbroker", label: "Subbroker & Associate Master", description: "Unified master for both ARN-holding sub-brokers AND non-ARN associates/referrers. Same underlying collection — role field distinguishes them. Fields: ARN, name, email, PAN, phone.", iconBg: "bg-emerald-500/10 text-emerald-400" },
      { id: "transfers", label: "Transfers", description: "Cross-folio unit transfers — inter-scheme, inter-broker, or inter-PAN moves. Preserves cost basis across folios.", iconBg: "bg-indigo-500/10 text-indigo-400" },
      { id: "gifts", label: "Gifts", description: "MF Gift In / Gift Out entries — TIG2/TIG3/TOG2/TOG3 transactions. Auto-derived from transaction_master.", iconBg: "bg-pink-500/10 text-pink-400" },
      { id: "transmissions", label: "Transmissions", description: "Transmission In / Transmission Out transactions. Auto-derived from transaction_master.", iconBg: "bg-purple-500/10 text-purple-400" },
      { id: "multi_broker_cases", label: "Multi-Broker Cases", description: "Folios where AUM Master and Transaction Master unit balances don't match. Excludes recent SIP/STP/SWP settlements.", iconBg: "bg-amber-500/10 text-amber-500" },
    ],
  },
  {
    title: "Reconciliation & Config",
    subtitle: "KYC, mismatches, logs & settings",
    icon: AlertTriangle,
    items: [
      { id: "kyc_mismatch", label: "KYC Mismatch", description: "PAN-level KYC discrepancies across sources — e.g. name spelled 'Krishna' at CAMS vs 'Krsna' at Karvy. Manual reconciliation queue.", iconBg: "bg-rose-500/10 text-rose-400" },
      { id: "kyc_missing", label: "KYC Missing", description: "Folios missing critical fields (PAN, Mobile, or Email). Blocks BSE order-placing and email communication.", iconBg: "bg-amber-500/10 text-amber-400" },
      { id: "reconciliation_jobs", label: "Reconciliation Jobs", description: "History of every reconciliation run — job_id, source, records processed, drift detected, duration.", iconBg: "bg-rose-500/10 text-rose-400" },
      { id: "column_mapping_config", label: "Column Mapping Config", description: "Rules that map raw CSV/DBF headers → canonical Master schema columns. Governs ingest transformations.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "upload_jobs", label: "Upload Jobs", description: "File upload history — every drag-drop or email-ingested file logs a row with status, size, processed count.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "scheme_master_logs", label: "Scheme Master Logs", description: "Scheme-master refresh cron execution log — timestamps, row counts, filtering stats per run.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "nav_backfill_status", label: "NAV Backfill Status", description: "Progress tracker for NAV seed / historical backfill jobs (per scheme, per phase).", iconBg: "bg-amber-500/10 text-amber-400" },
      { id: "dashboard_cache", label: "Dashboard Cache", description: "Top-level Dashboard aggregate cache — total AUM, cost, gain, XIRR. Rebuilt hourly + on-demand.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "dashboard_pan_metrics", label: "Dashboard PAN Metrics", description: "Per-client (PAN) cost / market / gain snapshot used for client-list drilldown. Rebuilt from classifier.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "dashboard_pan_metrics_backfill", label: "Dashboard PAN Metrics Backfill", description: "Bulk backfill job state for PAN metrics (progress, workers, ETA). Cleared once complete.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "dashboard_amc_details", label: "Dashboard AMC Details", description: "AMC-breakdown drilldown cache — when you click an AMC card, this table serves the client-list instantly.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "dashboard_aum_bracket_details", label: "Dashboard AUM Bracket Details", description: "AUM-bracket drilldown cache (<1L, 1-10L, 10L-1Cr, 1-5Cr, 5Cr+). Per-bracket client lists.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "dashboard_xirr_details", label: "Dashboard XIRR Details", description: "XIRR-by-holding drilldown cache — best/worst performing schemes across the book.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "dashboard_cache_rejected", label: "Dashboard Cache Rejected", description: "Rows rejected during cache refresh (bad data, missing NAV). Diagnostic — check when totals look off.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "exited_clients_full_cache", label: "Exited Clients Cache", description: "Precomputed roster of clients whose units are now zero — powers the 'Exited' tab on Portfolio.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "unit_mismatch_cache", label: "Unit Mismatch Cache", description: "Cache of folios where derived units (from transactions) don't match reported units (from aum_master).", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "multi_broker_cache", label: "Multi-Broker Cache", description: "Cached attribution for folios spanning multiple sub-brokers — used to split trail correctly.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "txn_master_reconciliation", label: "TXN Master Reconciliation", description: "Daily drift snapshot: raw source row counts vs Transaction Master row counts per source. Detects lost rows.", iconBg: "bg-rose-500/10 text-rose-400" },
      { id: "aum_active_drift_log", label: "AUM ↔ Active Drift Log", description: "Nightly drift log between aum_master (RTA-reported units) and active_units (derived from transactions).", iconBg: "bg-rose-500/10 text-rose-400" },
      { id: "email_report_tracker", label: "Email Report Tracker", description: "Per-email tracking of ingested CAMS/Karvy reports — reference number, status, retry count.", iconBg: "bg-amber-500/10 text-amber-400" },
      { id: "expected_reports", label: "Expected Reports", description: "Daily watchlist of reports the email scanner expects (WBTRN, WBCUM, WBR77, MFSD201 etc). Flags missing days.", iconBg: "bg-amber-500/10 text-amber-400" },
      { id: "flag_mapping_config", label: "Flag Mapping Config", description: "Transaction-flag → reflecting_in bucket mapping (Active Units / Sold Units / Non Financial / Rejection).", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "raw_column_order", label: "Raw Column Order", description: "Preserved original column ordering per raw source — used to re-export raw files without reordering.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "portfolio_categories", label: "Portfolio Categories", description: "Per-scheme category overrides (e.g. force-classify a hybrid as equity). Bypasses AMFI defaults.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "scan_status", label: "Scan Status", description: "Per-source ingestion scan state — last scan time, records-since-last-scan. Powers the ingest dashboard.", iconBg: "bg-gray-500/10 text-gray-400" },
    ],
  },
  {
    title: "Audit & History",
    subtitle: "Immutable logs & one-off backups",
    icon: History,
    items: [
      { id: "investor_audit_log", label: "Investor Audit Log", description: "Every field-level change to investor_master with before/after values. Compliance-critical — never delete on live.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "investor_master_safety_backup", label: "Investor Master (Safety Backup)", description: "Point-in-time snapshot of investor_master before a destructive migration. Rollback source of last resort.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "aum_master_safety_backup", label: "AUM Master (Safety Backup)", description: "Point-in-time snapshot of aum_master before a destructive migration. Rollback source of last resort.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "aum_master_staging_94fadcd2", label: "AUM Master Staging (drop me)", description: "One-off migration staging table left over from a specific migration run. Zero code references — safe to delete.", iconBg: "bg-red-500/10 text-red-400" },
      { id: "karvy_file_logs", label: "Karvy File Logs", description: "Audit of every Karvy raw file ingested — reference, size, row count, processing outcome.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "nav_cron_logs", label: "NAV Cron Logs", description: "Daily NAV-bot run log — start/end time, schemes updated, failures. Reference for troubleshooting stale NAV.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "scheme_returns_logs", label: "Scheme Returns Logs", description: "Scheme-returns computation cron log — trailing returns (1Y/3Y/5Y/SI) per scheme per run.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "agent_logs", label: "Agent Logs", description: "AI agent operation logs (any embedded LLM / classifier calls). Diagnostic for automation flows.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "multi_broker_auto_logs", label: "Multi-Broker Auto Logs", description: "Audit of automatic multi-broker match decisions — which folios were auto-attributed and on what evidence.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "impersonation_audit", label: "Impersonation Audit", description: "Log of every superuser 'view as client' action — user_id, target PAN, timestamp. Regulatory audit trail.", iconBg: "bg-rose-500/10 text-rose-400" },
      { id: "download_counters", label: "Download Counters", description: "Per-user tally of file exports (Excel / CSV / ZIP downloads) — used to enforce quota + billing.", iconBg: "bg-gray-500/10 text-gray-400" },
      { id: "subbroker_upload_history", label: "Subbroker Upload History", description: "History of every sub-broker file upload — who, when, what file, outcome.", iconBg: "bg-violet-500/10 text-violet-400" },
      { id: "password_resets", label: "Password Resets", description: "Password-reset request audit — token issued, timestamp, IP. Security-sensitive.", iconBg: "bg-rose-500/10 text-rose-400" },
      { id: "active_units", label: "Active Units (legacy)", description: "Legacy per-lot active-units ledger — obsolete since migration to aum_master + on-the-fly computation. Retained for grace period.", iconBg: "bg-red-500/10 text-red-400" },
    ],
  },
];

// Flatten all known collection IDs
const knownCollectionIds = new Set();
collectionGroups.forEach(g => g.items.forEach(i => knownCollectionIds.add(i.id)));

const DeleteCard = ({ item, count, isDeleting, disabled, onDelete }) => (
  <div
    className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition-all hover:shadow-md hover:border-gray-300"
    data-testid={`collection-row-${item.id}`}
    title={item.description}
  >
    <div className={`rounded-lg p-2 ${item.iconBg} flex-shrink-0`}>
      <HardDrive className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-gray-900 text-sm flex items-center gap-2">
        <span className="truncate">{item.label}</span>
        <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{item.id}</span>
      </div>
      <div className="text-[10px] text-gray-400 mt-0.5">{(count || 0).toLocaleString()} records</div>
    </div>
    <button
      onClick={() => onDelete(item.id)}
      disabled={count === 0 || isDeleting || disabled}
      className="p-2 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
      data-testid={`delete-${item.id}`}
    >
      {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
    </button>
  </div>
);

export default function ResetDB() {
  const [collectionCounts, setCollectionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [deletingCollection, setDeletingCollection] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [ensuringIndexes, setEnsuringIndexes] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleEnsureIndexes = async () => {
    setEnsuringIndexes(true);
    try {
      const resp = await fetch(`${API_URL}/api/database/ensure-indexes`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        toast.success("Indexes ensured", {
          description: `${data.created_count} created, ${data.skipped_count} already existed, ${data.failed_count} failed`,
        });
      } else {
        toast.error("Failed to ensure indexes", {
          description: data.detail || "An error occurred",
        });
      }
    } catch (error) {
      toast.error("Failed to ensure indexes", { description: String(error) });
    } finally {
      setEnsuringIndexes(false);
    }
  };

  const fetchCollectionCounts = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/database/collections-info`, {
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        setCollectionCounts(data.collections || {});
      }
    } catch (error) {
      console.error("Failed to fetch collection info:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCollectionCounts(); }, [fetchCollectionCounts]);


  const handleDeleteCollection = async (collectionName) => {
    const userInput = window.prompt(
      `This will permanently delete all records from "${collectionName}".\n\nType the collection name to confirm:`
    );
    if (userInput !== collectionName) {
      if (userInput !== null) toast.error("Cancelled - name did not match");
      return;
    }
    setDeletingCollection(collectionName);
    try {
      const resp = await fetch(`${API_URL}/api/data/clear-collection/${collectionName}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        toast.success(`Deleted ${data.deleted_count} records from ${collectionName}`);
        fetchCollectionCounts();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Failed to delete collection");
      }
    } catch (error) {
      toast.error("Failed to delete collection");
    } finally {
      setDeletingCollection(null);
    }
  };

  const handleDeleteAllData = async () => {
    const confirmText = "DELETE ALL DATA";
    const userInput = window.prompt(
      `WARNING: This will permanently delete ALL data including:\n\n` +
      `- All Client Holdings (CAMS & Karvy)\n` +
      `- All Transactions & Raw Data\n` +
      `- All KYC Mismatches\n` +
      `- Scheme Master & NAV Data\n` +
      `- All Cronjob Data & Logs\n\n` +
      `User accounts will be preserved.\n\n` +
      `Type "${confirmText}" to confirm:`
    );
    if (userInput !== confirmText) {
      if (userInput !== null) toast.error("Deletion cancelled - text did not match");
      return;
    }
    setDeletingAll(true);
    try {
      const resp = await fetch(`${API_URL}/api/data/all`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        toast.success("All data deleted successfully", { description: data.message });
        fetchCollectionCounts();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Failed to delete all data");
      }
    } catch (error) {
      toast.error("Failed to delete all data");
    } finally {
      setDeletingAll(false);
    }
  };

  const totalRecords = Object.values(collectionCounts).reduce((sum, c) => sum + (c || 0), 0);
  const totalCollections = Object.keys(collectionCounts).filter(c => (collectionCounts[c] || 0) > 0).length;
  const matchesSearch = (id, label) => !searchQuery || id.toLowerCase().includes(searchQuery.toLowerCase()) || label.toLowerCase().includes(searchQuery.toLowerCase());

  // Uncategorized collections — respects HIDDEN_COLLECTIONS so framework
  // runtime state (scheduler locks, dedupe flags, empty legacy tables)
  // never surfaces in the delete UI.
  const uncategorizedItems = Object.keys(collectionCounts)
    .filter(c => !knownCollectionIds.has(c) && !PROTECTED_COLLECTIONS.includes(c) && !HIDDEN_COLLECTIONS.has(c))
    .map(c => ({ id: c, label: c, description: `Uncategorized collection`, iconBg: "bg-gray-500/10 text-gray-400" }));

  return (
    <div className="p-6" data-testid="reset-db-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-teal-600" />
            Reset Database
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalCollections} collections &middot; {totalRecords.toLocaleString()} total records &middot; <span className="text-teal-600 font-medium">users ({collectionCounts["users"] || 0}) protected</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Looking for rebuild / refresh buttons? They now live on their respective pages — Dashboard "Refresh" (caches), Portfolio → Active Units tab (rebuild), Masters page (master regen).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleEnsureIndexes}
            disabled={ensuringIndexes}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-60"
            data-testid="ensure-indexes-button"
            title="Create all required DB indexes (compound indexes used by cron jobs). Idempotent — safe to run anytime."
          >
            {ensuringIndexes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            Ensure Indexes
          </button>
          <button
            onClick={handleDeleteAllData}
            disabled={deletingAll}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-60"
            data-testid="delete-all-data-button"
          >
            {deletingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete All Data
          </button>
        </div>
      </div>


      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search collections..."
          className="w-full max-w-sm pl-10 pr-4 py-2 border rounded-lg text-sm bg-white"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          data-testid="reset-db-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
          <span className="ml-2 text-gray-500">Loading collections...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {collectionGroups.map((group) => {
            const GroupIcon = group.icon;
            const visibleItems = group.items.filter(item => matchesSearch(item.id, item.label));
            if (visibleItems.length === 0) return null;
            return (
              <div key={group.title}>
                <div className="flex items-center gap-2 mb-4">
                  <GroupIcon className="w-4 h-4 text-gray-500" />
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{group.title}</h2>
                </div>
                <div className="space-y-2">
                  {visibleItems.map(item => (
                    <DeleteCard
                      key={item.id}
                      item={item}
                      count={collectionCounts[item.id]}
                      isDeleting={deletingCollection === item.id}
                      disabled={deletingAll}
                      onDelete={handleDeleteCollection}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Uncategorized */}
          {uncategorizedItems.filter(i => matchesSearch(i.id, i.label)).length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-4 h-4 text-gray-500" />
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Other</h2>
              </div>
              <div className="space-y-2">
                {uncategorizedItems.filter(i => matchesSearch(i.id, i.label)).map(item => (
                  <DeleteCard
                    key={item.id}
                    item={item}
                    count={collectionCounts[item.id]}
                    isDeleting={deletingCollection === item.id}
                    disabled={deletingAll}
                    onDelete={handleDeleteCollection}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
